/**
 * Unified App Page — combines app detail + download + install instructions on one page.
 *
 * Replaces the old split between AppDetail (protected, version list only) and
 * AppDownload (public, branded hero only). Now every app gets the full experience:
 *   - Branded hero with platform-specific download button + QR code
 *   - Version history (all platforms, expandable)
 *   - Installation guide (collapsible)
 *   - Features + trust badges
 *
 * Works for ALL apps — uses APP_CONFIGS for branded apps, generates a default
 * config for unconfigured apps via getConfigForApp().
 */
import { useState, useEffect } from 'react'
import { useParams, Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  Download,
  Smartphone,
  Apple,
  Calendar,
  HardDrive,
  QrCode,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  Code2,
  Terminal,
  Mic,
  BookOpen,
  Heart,
  Shield,
  Sparkles,
  Bot,
  Workflow,
  Brain,
  GitBranch,
  Zap,
  Camera,
  Music,
  Star,
  Users,
  Gift,
  Image,
  Video,
  Lock,
  Cloud,
} from 'lucide-react'
import { format } from 'date-fns'
import { getConfigForApp, type IconKey } from '../config/appDownloadConfigs'

interface AppVersion {
  version_name: string
  version_code: number
  platform: string
  file_size: number
  sha256: string
  released_at: string
  changelog: string
  download_url?: string
  min_os_version?: string
}

interface AppDetails {
  app_id: string
  name: string
  description: string
  bundle_id: string
  category: string
  icon_url: string
  versions: AppVersion[]
}

type Platform = 'android' | 'ios' | 'vscode' | 'cli' | 'web'

const ICON_MAP: Record<IconKey, React.ReactNode> = {
  'mic': <Mic className="w-6 h-6" />,
  'book-open': <BookOpen className="w-6 h-6" />,
  'heart': <Heart className="w-6 h-6" />,
  'shield': <Shield className="w-6 h-6" />,
  'sparkles': <Sparkles className="w-6 h-6" />,
  'bot': <Bot className="w-6 h-6" />,
  'workflow': <Workflow className="w-6 h-6" />,
  'brain': <Brain className="w-6 h-6" />,
  'git-branch': <GitBranch className="w-6 h-6" />,
  'zap': <Zap className="w-6 h-6" />,
  'camera': <Camera className="w-6 h-6" />,
  'music': <Music className="w-6 h-6" />,
  'star': <Star className="w-6 h-6" />,
  'users': <Users className="w-6 h-6" />,
  'gift': <Gift className="w-6 h-6" />,
  'rings': <Heart className="w-6 h-6" />,
  'image': <Image className="w-6 h-6" />,
  'video': <Video className="w-6 h-6" />,
  'lock': <Lock className="w-6 h-6" />,
  'cloud': <Cloud className="w-6 h-6" />,
}

function detectPlatform(appVersions?: AppVersion[]): Platform {
  // If app only has non-mobile platforms, default to those
  if (appVersions && appVersions.length > 0) {
    const platforms = new Set(appVersions.map(v => v.platform))
    if (platforms.has('vscode') && !platforms.has('android') && !platforms.has('ios')) return 'vscode'
    if (platforms.has('cli') && !platforms.has('android') && !platforms.has('ios')) return 'cli'
    if (platforms.has('web') && !platforms.has('android') && !platforms.has('ios')) return 'web'
  }
  const ua = navigator.userAgent.toLowerCase()
  if (/iphone|ipad|ipod/.test(ua)) return 'ios'
  return 'android'
}

export default function AppDetail() {
  const { appId: paramAppId } = useParams<{ appId: string }>()
  const location = useLocation()

  // Support both /apps/:appId and /download/:appId and shorthand routes like /mmb
  const appId = paramAppId || location.pathname.split('/').filter(Boolean).pop() || ''

  const [showInstructions, setShowInstructions] = useState(false)
  const [showAllVersions, setShowAllVersions] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [selectedPlatform, setSelectedPlatform] = useState<Platform>('android')

  const { data: app, isLoading } = useQuery<AppDetails>({
    queryKey: ['app', appId],
    queryFn: async () => {
      const res = await fetch(`/api/apps/${appId}`)
      return res.json()
    },
    enabled: !!appId,
  })

  useEffect(() => {
    if (app?.versions) setSelectedPlatform(detectPlatform(app.versions))
  }, [app])

  // Get config — branded if available, generated default otherwise
  const config = getConfigForApp(appId, app ? { name: app.name, description: app.description, icon_url: app.icon_url } : undefined)
  const t = config.theme

  // Version data — all platforms
  const versionsByPlatform = (platform: string) =>
    app?.versions?.filter((v) => v.platform === platform)?.sort((a, b) => b.version_code - a.version_code) || []

  const androidVersions = versionsByPlatform('android')
  const iosVersions = versionsByPlatform('ios')
  const vscodeVersions = versionsByPlatform('vscode')
  const cliVersions = versionsByPlatform('cli')
  const webVersions = versionsByPlatform('web')

  const latestAndroid = androidVersions[0]
  const latestIOS = iosVersions[0]
  const latestVSCode = vscodeVersions[0]
  const latestCLI = cliVersions[0]

  // Available platforms for this app
  const availablePlatforms: Platform[] = [
    ...(androidVersions.length > 0 ? ['android' as Platform] : []),
    ...(iosVersions.length > 0 ? ['ios' as Platform] : []),
    ...(vscodeVersions.length > 0 ? ['vscode' as Platform] : []),
    ...(cliVersions.length > 0 ? ['cli' as Platform] : []),
    ...(webVersions.length > 0 ? ['web' as Platform] : []),
  ]

  const androidVersion = latestAndroid?.version_name || config.defaultVersion
  const iosVersion = latestIOS?.version_name
  const vscodeVersion = latestVSCode?.version_name
  const cliVersion = latestCLI?.version_name

  const appVersion = selectedPlatform === 'ios' && iosVersion ? iosVersion
    : selectedPlatform === 'vscode' && vscodeVersion ? vscodeVersion
    : selectedPlatform === 'cli' && cliVersion ? cliVersion
    : androidVersion

  const androidFileSize = latestAndroid?.file_size
    ? `${(latestAndroid.file_size / 1024 / 1024).toFixed(0)} MB`
    : config.defaultFileSize

  const iosFileSize = latestIOS?.file_size
    ? `${(latestIOS.file_size / 1024 / 1024).toFixed(0)} MB`
    : null

  const vscodeFileSize = latestVSCode?.file_size
    ? latestVSCode.file_size >= 1_000_000
      ? `${(latestVSCode.file_size / 1_000_000).toFixed(1)} MB`
      : `${(latestVSCode.file_size / 1_000).toFixed(0)} KB`
    : null

  // Download URLs — prefer API redirect (authoritative, logs download, uses current PUBLIC_DOWNLOAD_URL)
  // Fall back to stored download_url from API, then construct from version metadata
  const downloadUrl = latestAndroid
    ? `/api/apps/${appId}/download/${latestAndroid.version_name}/android`
    : `${config.downloadPath}/v${config.defaultVersion}-1.apk`

  const vscodeDownloadUrl = latestVSCode?.download_url
    || (latestVSCode ? `/downloads/mskai-apps/vscode/${appId}/v${latestVSCode.version_name}-${latestVSCode.version_code}.vsix` : null)

  const cliDownloadUrl = latestCLI?.download_url
    || (latestCLI ? `/downloads/mskai-apps/cli/${appId}/${appId}-${latestCLI.version_name}.tar.gz` : null)

  const iosManifestUrl = config.iosManifestPath

  const handleAndroidDownload = () => {
    setDownloading(true)
    // Redirect to API download endpoint — logs download + redirects to MinIO via PUBLIC_DOWNLOAD_URL
    window.location.href = downloadUrl
    setTimeout(() => setDownloading(false), 3000)
  }

  const handleIOSDownload = () => {
    setDownloading(true)
    if (latestIOS) {
      fetch(`/api/apps/${appId}/download/${latestIOS.version_name}/ios`).catch(() => {})
    }
    const manifestFullUrl = `${window.location.origin}${iosManifestUrl}`
    window.location.href = `itms-services://?action=download-manifest&url=${encodeURIComponent(manifestFullUrl)}`
    setTimeout(() => setDownloading(false), 3000)
  }

  const handleVSCodeDownload = () => {
    setDownloading(true)
    if (latestVSCode) {
      fetch(`/api/apps/${appId}/download/${latestVSCode.version_name}/vscode`).catch(() => {})
    }
    if (vscodeDownloadUrl) window.location.href = vscodeDownloadUrl
    setTimeout(() => setDownloading(false), 3000)
  }

  const handleCLIDownload = () => {
    setDownloading(true)
    if (latestCLI) {
      fetch(`/api/apps/${appId}/download/${latestCLI.version_name}/cli`).catch(() => {})
    }
    if (cliDownloadUrl) window.location.href = cliDownloadUrl
    setTimeout(() => setDownloading(false), 3000)
  }

  const handleVersionDownload = (version: AppVersion) => {
    // Log download (fire-and-forget)
    fetch(`/api/apps/${appId}/download/${version.version_name}/${version.platform}`).catch(() => {})

    if (version.platform === 'ios') {
      const manifestFullUrl = `${window.location.origin}${iosManifestUrl}`
      window.location.href = `itms-services://?action=download-manifest&url=${encodeURIComponent(manifestFullUrl)}`
    } else if (version.platform === 'vscode') {
      window.location.href = version.download_url
        || `/downloads/mskai-apps/vscode/${appId}/v${version.version_name}-${version.version_code}.vsix`
    } else if (version.platform === 'cli') {
      window.location.href = version.download_url
        || `/downloads/mskai-apps/cli/${appId}/${appId}-${version.version_name}.tar.gz`
    } else {
      window.location.href = version.download_url
        || `/downloads/mskai-apps/${version.platform}/${appId}/v${version.version_name}-${version.version_code}.apk`
    }
  }

  const androidInstallSteps = [
    { step: 1, title: 'Download the APK', description: 'Click the download button above. Your browser may ask you to confirm the download.' },
    { step: 2, title: 'Enable Unknown Sources', description: 'Go to Settings > Security > Install unknown apps and enable it for your browser or file manager.' },
    { step: 3, title: 'Open the APK File', description: 'Find the downloaded file in your Downloads folder and tap to open it.' },
    { step: 4, title: 'Install the App', description: 'Tap "Install" when prompted. The installation will complete in a few seconds.' },
    { step: 5, title: 'Launch & Sign In', description: config.androidFinalStep },
  ]

  const iosInstallSteps = [
    { step: 1, title: 'Device Registration Required', description: 'Your iPhone/iPad must be registered with our Apple Developer account. Contact us if you haven\'t done this.' },
    { step: 2, title: 'Tap "Install on iOS"', description: 'Click the install button above. Safari will prompt you to install the app.' },
    { step: 3, title: 'Trust the Developer', description: 'Go to Settings > General > VPN & Device Management, find our profile and tap "Trust".' },
    { step: 4, title: 'Launch the App', description: config.iosFinalStep },
  ]

  const installSteps = selectedPlatform === 'ios' ? iosInstallSteps : androidInstallSteps

  // Only block render if load completed and app not found. While loading, render
  // config-driven content (tagline, features) immediately so tests and users see
  // branding without waiting for the API round-trip.
  if (!isLoading && !app) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">App not found</p>
        <Link to="/apps" className="text-primary-500 mt-2 inline-block">Back to Apps</Link>
      </div>
    )
  }

  // Versions to show in the history section — latest only by default, full history on expand
  const platformVersions = selectedPlatform === 'vscode' ? vscodeVersions
    : selectedPlatform === 'cli' ? cliVersions
    : selectedPlatform === 'ios' ? iosVersions
    : androidVersions
  const visibleVersions = showAllVersions ? platformVersions : platformVersions.slice(0, 1)

  return (
    <div className="min-h-screen">
      {/* Back nav + public download link (only shown when coming from /apps) */}
      {location.pathname.startsWith('/apps') && (
        <div className="max-w-5xl mx-auto px-4 pt-4 flex items-center justify-between">
          <Link
            to="/apps"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Apps
          </Link>
          <a
            href={`/download/${appId}`}
            className="inline-flex items-center gap-2 text-primary-400 hover:text-primary-300 transition-colors text-sm"
          >
            <ExternalLink className="w-4 h-4" />
            View Download Page
          </a>
        </div>
      )}

      {/* ── Hero Section ── */}
      <section className="relative overflow-hidden py-12 md:py-16 px-4">
        <div className={`absolute inset-0 ${t.heroBg}`} />

        <div className="relative max-w-4xl mx-auto text-center">
          {/* App Icon */}
          <div className="mb-6 flex justify-center">
            <div className="relative">
              {app?.icon_url ? (
                <img
                  src={app.icon_url}
                  alt={app.name}
                  className={`w-28 h-28 md:w-32 md:h-32 rounded-3xl shadow-2xl ${t.iconShadow}`}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none'
                  }}
                />
              ) : (
                <div className={`w-28 h-28 md:w-32 md:h-32 rounded-3xl shadow-2xl bg-gradient-to-br ${t.iconFallbackGradient} flex items-center justify-center`}>
                  <span className="text-4xl md:text-5xl font-bold text-white">{(app?.name || config.name)[0]}</span>
                </div>
              )}
              <div className="absolute -bottom-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                v{appVersion}
              </div>
            </div>
          </div>

          {/* Title & Description */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3">
            <span className={t.titleGradient}>{app?.name || config.name}</span>
          </h1>
          <p className="text-sm md:text-lg text-gray-300 mb-1">{config.tagline}</p>
          <p className="text-xs md:text-sm text-gray-400 max-w-2xl mx-auto mb-2">
            {app?.description || config.description}
          </p>

          {/* Metadata badges */}
          <div className="flex flex-wrap gap-2 justify-center mb-6">
            {app?.category && (
              <span className="bg-primary-500/20 text-primary-400 px-3 py-1 rounded-full text-xs capitalize">
                {app.category}
              </span>
            )}
            {app?.bundle_id && (
              <span className="bg-white/10 text-gray-400 px-3 py-1 rounded-full text-xs font-mono">
                {app.bundle_id}
              </span>
            )}
            {app && (
              <span className="bg-white/10 text-gray-300 px-3 py-1 rounded-full text-xs">
                {app.versions?.length || 0} versions
              </span>
            )}
          </div>

          {/* Platform Selector — dynamic based on available versions */}
          <div className="flex flex-wrap justify-center gap-2 mb-6" data-testid="platform-selector">
            {availablePlatforms.includes('android') && (
              <button
                type="button"
                data-testid="platform-android"
                onClick={() => setSelectedPlatform('android')}
                className={`px-4 md:px-6 py-3 rounded-xl font-medium flex items-center gap-2 transition-all min-h-[44px] text-sm md:text-base ${
                  selectedPlatform === 'android'
                    ? 'bg-green-500/20 border-2 border-green-500 text-green-400'
                    : 'bg-white/5 border border-white/10 text-gray-400 hover:border-white/30'
                }`}
              >
                <Smartphone className="w-5 h-5 flex-shrink-0" />
                Android
              </button>
            )}
            {availablePlatforms.includes('ios') && (
              <button
                type="button"
                data-testid="platform-ios"
                onClick={() => setSelectedPlatform('ios')}
                className={`px-4 md:px-6 py-3 rounded-xl font-medium flex items-center gap-2 transition-all min-h-[44px] text-sm md:text-base ${
                  selectedPlatform === 'ios'
                    ? 'bg-gray-500/20 border-2 border-gray-400 text-gray-200'
                    : 'bg-white/5 border border-white/10 text-gray-400 hover:border-white/30'
                }`}
              >
                <Apple className="w-5 h-5 flex-shrink-0" />
                iOS
              </button>
            )}
            {availablePlatforms.includes('vscode') && (
              <button
                type="button"
                data-testid="platform-vscode"
                onClick={() => setSelectedPlatform('vscode')}
                className={`px-4 md:px-6 py-3 rounded-xl font-medium flex items-center gap-2 transition-all min-h-[44px] text-sm md:text-base ${
                  selectedPlatform === 'vscode'
                    ? 'bg-blue-500/20 border-2 border-blue-500 text-blue-400'
                    : 'bg-white/5 border border-white/10 text-gray-400 hover:border-white/30'
                }`}
              >
                <Code2 className="w-5 h-5 flex-shrink-0" />
                VS Code
              </button>
            )}
            {availablePlatforms.includes('cli') && (
              <button
                type="button"
                data-testid="platform-cli"
                onClick={() => setSelectedPlatform('cli')}
                className={`px-4 md:px-6 py-3 rounded-xl font-medium flex items-center gap-2 transition-all min-h-[44px] text-sm md:text-base ${
                  selectedPlatform === 'cli'
                    ? 'bg-amber-500/20 border-2 border-amber-500 text-amber-400'
                    : 'bg-white/5 border border-white/10 text-gray-400 hover:border-white/30'
                }`}
              >
                <Terminal className="w-5 h-5 flex-shrink-0" />
                CLI
              </button>
            )}
          </div>

          {/* ── Download Button — Android ── */}
          {selectedPlatform === 'android' && (
            <div className="flex flex-col items-center gap-4 mb-6">
              <button
                type="button"
                onClick={handleAndroidDownload}
                disabled={downloading}
                className={`group relative px-8 py-4 rounded-2xl font-bold text-lg bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 hover:from-green-600 hover:via-emerald-600 hover:to-teal-600 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-green-500/30 hover:shadow-green-500/50 flex items-center gap-3 ${downloading ? 'opacity-75 cursor-wait' : ''}`}
              >
                <Download className={`w-6 h-6 ${downloading ? 'animate-bounce' : 'group-hover:animate-bounce'}`} />
                {downloading ? 'Starting Download...' : 'Download for Android'}
                <Smartphone className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-4 text-sm text-gray-400">
                <span>Version {androidVersion}</span>
                <span>&bull;</span>
                <span>{androidFileSize}</span>
                <span>&bull;</span>
                <span>Android 7.0+</span>
              </div>
            </div>
          )}

          {/* ── Download Button — iOS ── */}
          {selectedPlatform === 'ios' && (
            <div className="flex flex-col items-center gap-4 mb-6">
              {iosVersion ? (
                <>
                  <button
                    type="button"
                    onClick={handleIOSDownload}
                    disabled={downloading}
                    className={`group relative px-8 py-4 rounded-2xl font-bold text-lg bg-gradient-to-r from-gray-600 via-gray-500 to-gray-600 hover:from-gray-500 hover:via-gray-400 hover:to-gray-500 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-gray-500/30 hover:shadow-gray-500/50 flex items-center gap-3 ${downloading ? 'opacity-75 cursor-wait' : ''}`}
                  >
                    <Download className={`w-6 h-6 ${downloading ? 'animate-bounce' : 'group-hover:animate-bounce'}`} />
                    {downloading ? 'Installing...' : 'Install on iOS'}
                    <Apple className="w-5 h-5" />
                  </button>
                  <div className="flex items-center gap-4 text-sm text-gray-400">
                    <span>Version {iosVersion}</span>
                    {iosFileSize && (<><span>&bull;</span><span>{iosFileSize}</span></>)}
                    <span>&bull;</span>
                    <span>iOS 14.0+</span>
                  </div>
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm max-w-md">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                      <span className="text-amber-200">
                        Your device must be registered with our developer account to install this app.
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-6 max-w-md">
                    <Apple className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                    <h3 className="text-lg font-medium mb-2">iOS Coming Soon</h3>
                    <p className="text-gray-400 text-sm">
                      The iOS version is currently being prepared. Download the Android version or check back soon.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Download Button — VS Code Extension ── */}
          {selectedPlatform === 'vscode' && (
            <div className="flex flex-col items-center gap-4 mb-6">
              {vscodeDownloadUrl ? (
                <>
                  <button
                    type="button"
                    onClick={handleVSCodeDownload}
                    disabled={downloading}
                    className={`group relative px-8 py-4 rounded-2xl font-bold text-lg bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 hover:from-blue-600 hover:via-indigo-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 flex items-center gap-3 ${downloading ? 'opacity-75 cursor-wait' : ''}`}
                  >
                    <Download className={`w-6 h-6 ${downloading ? 'animate-bounce' : 'group-hover:animate-bounce'}`} />
                    {downloading ? 'Starting Download...' : 'Download .vsix'}
                    <Code2 className="w-5 h-5" />
                  </button>
                  <div className="flex items-center gap-4 text-sm text-gray-400">
                    <span>Version {vscodeVersion}</span>
                    {vscodeFileSize && (<><span>&bull;</span><span>{vscodeFileSize}</span></>)}
                    <span>&bull;</span>
                    <span>VS Code {latestVSCode?.min_os_version || '1.84.0'}+</span>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-sm max-w-md">
                    <p className="text-blue-200">
                      <strong>Install:</strong> Open VS Code → Extensions → <code className="bg-white/10 px-1 rounded">...</code> menu →
                      Install from VSIX → select the downloaded file.
                    </p>
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-6 max-w-md">
                    <Code2 className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                    <h3 className="text-lg font-medium mb-2">No .vsix Available</h3>
                    <p className="text-gray-400 text-sm">No extension binary has been uploaded yet.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Download Button — CLI Tool ── */}
          {selectedPlatform === 'cli' && (
            <div className="flex flex-col items-center gap-4 mb-6">
              {cliDownloadUrl ? (
                <>
                  <button
                    type="button"
                    onClick={handleCLIDownload}
                    disabled={downloading}
                    className={`group relative px-8 py-4 rounded-2xl font-bold text-lg bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 hover:from-amber-600 hover:via-orange-600 hover:to-red-600 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 flex items-center gap-3 ${downloading ? 'opacity-75 cursor-wait' : ''}`}
                  >
                    <Download className={`w-6 h-6 ${downloading ? 'animate-bounce' : 'group-hover:animate-bounce'}`} />
                    {downloading ? 'Starting Download...' : 'Download CLI'}
                    <Terminal className="w-5 h-5" />
                  </button>
                  <div className="flex items-center gap-4 text-sm text-gray-400">
                    <span>Version {cliVersion}</span>
                    <span>&bull;</span>
                    <span>Python 3.10+</span>
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-6 max-w-md">
                    <Terminal className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                    <h3 className="text-lg font-medium mb-2">No CLI Build Available</h3>
                    <p className="text-gray-400 text-sm">No CLI binary has been uploaded yet.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* QR Code */}
          {selectedPlatform === 'android' && (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 max-w-xs mx-auto mb-6">
              <div className="flex items-center justify-center gap-2 mb-3">
                <QrCode className={`w-4 h-4 ${t.accentText}`} />
                <span className="font-medium text-sm">Scan to Download</span>
              </div>
              <div className="bg-white p-3 rounded-xl inline-block">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(`https://appstore.multiskilled.ai${downloadUrl}`)}`}
                  alt="QR Code"
                  className="w-32 h-32"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">Scan with your phone camera</p>
            </div>
          )}
        </div>
      </section>

      {/* ── Version History ── */}
      <section className="py-8 px-4 border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold mb-4 flex items-center gap-2">
            {selectedPlatform === 'android' ? (
              <Smartphone className="w-5 h-5 text-green-400" />
            ) : (
              <Apple className="w-5 h-5 text-gray-400" />
            )}
            Release History
          </h2>

          {platformVersions.length > 0 ? (
            <div className="space-y-3">
              {visibleVersions.map((version) => (
                <div
                  key={`${version.version_name}-${version.platform}-${version.version_code}`}
                  className="bg-white/5 border border-white/10 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-semibold">v{version.version_name}</span>
                      <span className="text-xs text-gray-500 bg-white/5 px-2 py-0.5 rounded">Build {version.version_code}</span>
                      {version === (selectedPlatform === 'android' ? latestAndroid : latestIOS) && (
                        <span className="text-xs text-green-400 bg-green-500/10 px-2 py-0.5 rounded">Latest</span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {format(new Date(version.released_at), 'MMM d, yyyy')}
                      </span>
                      {version.file_size > 0 && (
                        <span className="flex items-center gap-1">
                          <HardDrive className="w-3 h-3" />
                          {(version.file_size / 1024 / 1024).toFixed(1)} MB
                        </span>
                      )}
                    </div>
                    {version.changelog && (
                      <p className="text-sm text-gray-500 mt-2 line-clamp-2">{version.changelog}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleVersionDownload(version)}
                    className={`flex-shrink-0 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors min-h-[44px] text-sm font-medium ${
                      version.platform === 'android'
                        ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                        : 'bg-gray-500/20 text-gray-300 hover:bg-gray-500/30'
                    }`}
                  >
                    <Download className="w-4 h-4" />
                    Download {version.platform === 'android' ? 'APK' : version.platform === 'ios' ? 'IPA' : version.platform === 'vscode' ? 'VSIX' : version.platform.toUpperCase()}
                  </button>
                </div>
              ))}

              {platformVersions.length > 1 && (
                <button
                  type="button"
                  onClick={() => setShowAllVersions(!showAllVersions)}
                  className="w-full text-center py-2 text-sm text-gray-400 hover:text-white transition-colors flex items-center justify-center gap-1"
                >
                  {showAllVersions ? (
                    <>Hide version history <ChevronUp className="w-4 h-4" /></>
                  ) : (
                    <>Version history ({platformVersions.length - 1} older) <ChevronDown className="w-4 h-4" /></>
                  )}
                </button>
              )}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No releases available yet.</p>
          )}
        </div>
      </section>

      {/* ── Installation Guide ── */}
      <section className="py-8 px-4 border-t border-white/10">
        <div className="max-w-3xl mx-auto">
          <button
            type="button"
            onClick={() => setShowInstructions(!showInstructions)}
            className="w-full bg-white/5 border border-white/10 rounded-xl p-4 md:p-6 flex items-center justify-between hover:bg-white/10 transition-colors"
          >
            <div className="flex items-center gap-3">
              {selectedPlatform === 'ios' ? (
                <Apple className="w-5 h-5 text-gray-400" />
              ) : (
                <Smartphone className="w-5 h-5 text-green-400" />
              )}
              <span className="font-semibold text-base md:text-lg">
                {selectedPlatform === 'ios' ? 'iOS' : 'Android'} Installation Guide
              </span>
            </div>
            {showInstructions ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {showInstructions && (
            <div className="mt-4 bg-white/5 border border-white/10 rounded-xl p-6 space-y-6">
              {selectedPlatform === 'android' ? (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-sm">
                  <p className="text-yellow-200">
                    <strong>Note:</strong> Since this app is distributed outside the Play Store,
                    you'll need to enable installation from unknown sources. {config.unknownSourceNote}
                  </p>
                </div>
              ) : (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-sm">
                  <p className="text-blue-200">
                    <strong>Note:</strong> iOS apps distributed outside the App Store require your device's UDID to be registered
                    with our Apple Developer account. Contact us to register your device.
                  </p>
                </div>
              )}

              {installSteps.map((item, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className={`flex-shrink-0 w-8 h-8 ${t.stepBg} rounded-full flex items-center justify-center text-sm font-bold`}>
                    {item.step}
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">{item.title}</h4>
                    <p className="text-gray-400 text-sm">{item.description}</p>
                  </div>
                </div>
              ))}

              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-green-200 font-medium mb-1">You're all set!</p>
                  <p className="text-green-300/70">{config.completionMessage}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── Features ── */}
      {config.features.length > 0 && (
        <section className="py-8 md:py-12 px-4 border-t border-white/10">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-8">
              Why People Love <span className={t.accentText}>{app?.name || config.name}</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {config.features.map((feature, idx) => (
                <div
                  key={idx}
                  className={`bg-white/5 border border-white/10 rounded-xl p-4 md:p-6 ${t.featureBorder} transition-colors`}
                >
                  <div className={`${t.accentText} mb-3`}>{ICON_MAP[feature.iconKey]}</div>
                  <h3 className="font-semibold text-base mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-xs md:text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Trust Badges ── */}
      {config.trustBadges.length > 0 && (
        <section className="py-8 px-4 border-t border-white/10">
          <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-8 text-center">
            {config.trustBadges.map((badge, idx) => (
              <div key={idx}>
                <div className={`text-2xl md:text-3xl font-bold ${t.accentText}`}>{badge.value}</div>
                <div className="text-sm text-gray-500">{badge.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Direct Link Footer ── */}
      <section className="py-6 px-4 border-t border-white/10 bg-black/20">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-500 text-sm mb-2">Direct download link:</p>
          {selectedPlatform === 'android' ? (
            <a
              href={downloadUrl}
              className={`${t.accentText} hover:opacity-80 text-sm flex items-center justify-center gap-1`}
            >
              appstore.multiskilled.ai{downloadUrl}
              <ExternalLink className="w-3 h-3" />
            </a>
          ) : iosVersion ? (
            <span className="text-gray-400 text-sm">
              iOS installation available via the Install button above (requires registered device)
            </span>
          ) : (
            <span className="text-gray-500 text-sm">iOS version coming soon</span>
          )}
        </div>
      </section>
    </div>
  )
}
